/**
 * PinPlacementOverlay Component
 *
 * Displays an overlay UI when pin placement mode is active.
 * Shows instructions, cursor coordinates, action buttons, and a cursor-following
 * pin preview marker.
 *
 * Phase 4: Pin Placement Mode Implementation
 * Phase 7: Accessibility enhancements (keyboard navigation, screen reader support)
 *         - Cursor-following pin preview marker
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MapPin as MapPinType } from '../MapComponent.types';
import {
  mapControlButtonClass,
  mapControlDividerClass,
  mapControlInputClass,
  mapControlSurfaceClass,
} from './mapControlStyles';

/**
 * Props for PinPlacementOverlay component
 */
export interface PinPlacementOverlayProps {
  /** Whether pin placement mode is active */
  isActive: boolean;
  /** Pending pin data (for preview) */
  pendingPin?: Partial<MapPinType>;
  /** Current cursor position [lng, lat] */
  cursorPosition: [number, number] | null;
  /** Callback when user confirms placement */
  onConfirm: (coordinates: [number, number]) => void;
  /** Callback when user cancels placement */
  onCancel: () => void;
  /** Callback to update pending pin properties */
  onUpdatePin?: (updates: Partial<MapPinType>) => void;
  /** Reference to the map container for cursor-following preview positioning */
  mapContainerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Format coordinate for display (4 decimal places)
 */
function formatCoordinate(value: number): string {
  return value.toFixed(4);
}

/**
 * PinPlacementOverlay - Shows UI overlay during pin placement mode
 *
 * Includes:
 * - Top instruction banner with pin preview and keyboard shortcuts
 * - Cursor-following pin marker that shows where the pin will be placed
 * - Bottom coordinates display showing current lat/lng
 */
export const PinPlacementOverlay: React.FC<PinPlacementOverlayProps> = ({
  isActive,
  pendingPin,
  cursorPosition,
  onConfirm,
  onCancel,
  mapContainerRef,
}) => {
  // Ref for screen reader announcements
  const announcementRef = useRef<HTMLDivElement>(null);

  // Track mouse position relative to map container for cursor-following preview
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

  // Handle mouse move for cursor-following preview
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!mapContainerRef?.current) return;
    const rect = mapContainerRef.current.getBoundingClientRect();
    // Account for CSS transform scale (editor canvas zoom)
    const scaleX = rect.width / mapContainerRef.current.offsetWidth || 1;
    const scaleY = rect.height / mapContainerRef.current.offsetHeight || 1;
    setMousePosition({
      x: (e.clientX - rect.left) / scaleX,
      y: (e.clientY - rect.top) / scaleY,
    });
  }, [mapContainerRef]);

  // Handle mouse leave to hide preview when cursor leaves map
  const handleMouseLeave = useCallback(() => {
    setMousePosition(null);
  }, []);

  // Attach mouse event listeners to map container
  useEffect(() => {
    if (!isActive || !mapContainerRef?.current) return;

    const container = mapContainerRef.current;
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isActive, mapContainerRef, handleMouseMove, handleMouseLeave]);

  // Announce when placement mode is entered
  useEffect(() => {
    if (isActive && announcementRef.current) {
      // Delay announcement slightly to ensure it's picked up by screen readers
      const timer = setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent =
            'Pin placement mode active. Use arrow keys to adjust position, Enter to confirm, or Escape to cancel.';
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isActive]);

  if (!isActive) return null;

  const pinColor = pendingPin?.style?.color || '#1E3A5F';
  const pinScale = pendingPin?.style?.scale || 1.0;

  return (
    <>
      {/* Screen reader announcement region */}
      <div
        ref={announcementRef}
        className="sr-only"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      />

      {/* Top instruction banner */}
      <div
        className={cn(
          "absolute top-3 left-1/2 transform -translate-x-1/2 z-10 px-4 py-2.5 flex items-center gap-3",
          mapControlSurfaceClass,
        )}
        role="status"
        aria-live="polite"
      >
        {/* Pin preview */}
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full"
          style={{ backgroundColor: `${pinColor}20` }}
        >
          <MapPin
            className="w-5 h-5"
            style={{ color: pinColor }}
            aria-hidden="true"
          />
        </div>

        {/* Instructions */}
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">
            {pendingPin?.name || 'Place Pin'}
          </span>
          <span className="text-xs text-muted-foreground">
            Click on map to place •{' '}
            <kbd className={cn("px-1 py-0.5 rounded text-[10px]", mapControlInputClass)}>↑↓←→</kbd> fine-tune •{' '}
            <kbd className={cn("px-1 py-0.5 rounded text-[10px]", mapControlInputClass)}>Esc</kbd> cancel •{' '}
            <kbd className={cn("px-1 py-0.5 rounded text-[10px]", mapControlInputClass)}>Enter</kbd> confirm
          </span>
        </div>

        {/* Action buttons */}
        <div className={cn("flex items-center gap-1 ml-2 border-l pl-3", mapControlDividerClass)}>
          <button
            onClick={onCancel}
            className={cn("p-1.5 rounded-md transition-colors", mapControlButtonClass)}
            aria-label="Cancel pin placement (Escape)"
            title="Cancel (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={() => cursorPosition && onConfirm(cursorPosition)}
            disabled={!cursorPosition}
            className="p-1.5 rounded-md border border-green-500/30 bg-green-500/10 text-green-300
                       hover:bg-green-500/15 hover:text-green-200 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Confirm pin placement (Enter)"
            title="Confirm (Enter)"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Bottom coordinates display */}
      {cursorPosition && (
        <div
          className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10
                     bg-black/75 backdrop-blur-sm rounded-md px-3 py-1.5
                     text-white text-xs font-mono"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="text-white/60 mr-2">Lat:</span>
          <span>{formatCoordinate(cursorPosition[1])}</span>
          <span className="text-white/60 mx-2">Lng:</span>
          <span>{formatCoordinate(cursorPosition[0])}</span>
        </div>
      )}

      {/* Hint for mobile (no cursor position available) */}
      {!cursorPosition && (
        <div
          className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10
                     bg-black/75 backdrop-blur-sm rounded-md px-3 py-1.5
                     text-white text-xs"
        >
          Move cursor over map to see coordinates
        </div>
      )}

      {/* Cursor-following pin preview marker */}
      {mousePosition && (
        <div
          className="absolute pointer-events-none z-20"
          style={{
            left: mousePosition.x,
            top: mousePosition.y,
            transform: `translate(-50%, -100%) scale(${pinScale})`,
          }}
          aria-hidden="true"
        >
          {/* Pin drop shadow for depth */}
          <div
            className="absolute w-3 h-1 rounded-full opacity-30"
            style={{
              backgroundColor: '#000',
              left: '50%',
              bottom: '-2px',
              transform: 'translateX(-50%)',
              filter: 'blur(2px)',
            }}
          />
          {/* Pin marker SVG */}
          <svg
            width="32"
            height="40"
            viewBox="0 0 32 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="drop-shadow-lg"
            style={{
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            }}
          >
            {/* Pin body */}
            <path
              d="M16 0C7.163 0 0 7.163 0 16c0 8.837 16 24 16 24s16-15.163 16-24C32 7.163 24.837 0 16 0z"
              fill={pinColor}
            />
            {/* Inner white circle */}
            <circle cx="16" cy="14" r="6" fill="white" fillOpacity="0.9" />
            {/* Center dot */}
            <circle cx="16" cy="14" r="3" fill={pinColor} />
          </svg>
          {/* Pulse animation ring */}
          <div
            className="absolute rounded-full animate-ping"
            style={{
              width: '12px',
              height: '12px',
              backgroundColor: pinColor,
              opacity: 0.4,
              left: '50%',
              top: '10px',
              transform: 'translateX(-50%)',
            }}
          />
        </div>
      )}
    </>
  );
};

export default PinPlacementOverlay;
